/**
 * PATH: packages/ui/src/components/global-search/telemetry.ts
 * FILE_ID: UI-GLOBALSEARCH-TELEMETRY-001
 * PURPOSE: Fire-and-forget search telemetry — zero-result queries and
 *          result selections — emitted at the two contract seams of the
 *          palette (final merge + row selection).
 * WHY: Search-architecture audit 2026-07-06: relevance bugs (e.g. "quote
 *      builder" returning zero Pages rows for weeks) were invisible until
 *      an operator complained. Zero-result queries are the canonical
 *      leading indicator; selection events (query → what was picked, at
 *      which rank) are the input every later relevance/frecency tuning
 *      needs. Emission is at the SEAM, not inside providers — providers
 *      stay pure.
 * FLOW:
 *   useSearch final merge ─┐
 *   row click / Enter ─────┴→ emitSearchTelemetry → window.__QT_OBS__ ring
 *                                                 → CustomEvent for hosts
 * DEPENDENCIES: none (browser globals only, guarded — SSR/test safe).
 * RELATED:
 *  - useSearch.ts (zero-result emit), SearchResults.tsx + useSearch Enter
 *    handler (selection emit)
 *  - U2 (search-unification): email-inbox / kanban-board / jobs-table
 *    layers reuse THIS stream via createSearchQueryResolvedEmitter —
 *    never fork a second telemetry module.
 */

export interface SearchTelemetryZeroResults {
  kind: 'zero_results'
  query: string
  /** Provider ids consulted for this query (post chip-filtering). */
  providerIds: string[]
  /** Chips active when the query returned nothing. */
  activeFilters: string[]
}

export interface SearchTelemetryResultSelected {
  kind: 'result_selected'
  query: string
  /** Rank the row held when picked (0-based, visible slice). */
  index: number
  resultId: string
  resultType: string
  providerId?: string
}

export interface SearchTelemetryQueryResolved {
  kind: 'query_resolved'
  query: string
  /** Which search layer answered (e.g. 'email-inbox', 'kanban-board',
   *  'jobs-table'). The palette's own events carry no surface (legacy
   *  schema, pre-U2); every non-palette layer MUST set one. */
  surface: string
  resultCount: number
  zeroResults: boolean
  /** Whether the request carried the relevance opt-in (`rank=1`,
   *  be!4448) — lets the ranking flip be A/B'd against zero-result
   *  rates from the same stream. Only email surfaces set it. */
  rankParamSent?: boolean
}

export interface SearchTelemetryTiming {
  kind: 'search_timing'
  query: string
  /** Full fan-out wall time (ms) — first keystroke-debounce fire to the
   *  final authoritative merge. */
  totalMs: number
  /** Per-provider settle time (ms). Cache hits report 0. */
  providerMs: Record<string, number>
  /** Providers answered from the session query cache (no network). */
  cacheHits: string[]
  resultCount: number
}

export type SearchTelemetryEvent = (
  | SearchTelemetryZeroResults
  | SearchTelemetryResultSelected
  | SearchTelemetryTiming
  | SearchTelemetryQueryResolved
) & {
  /** Epoch ms, stamped by the emitter. */
  ts?: number
  /** Optional on the legacy palette kinds; see
   *  SearchTelemetryQueryResolved.surface for semantics. */
  surface?: string
}

/** DOM event name hosts can listen on to forward into their own
 *  analytics pipe (`analytics.track` on the master SPA, etc.). */
export const SEARCH_TELEMETRY_EVENT = 'quotech:search-telemetry'

/** Ring-buffer cap on the obs-seam window slot — bounded so a long
 *  session can never grow memory unbounded. */
const OBS_RING_MAX = 200

interface QtObsSlot {
  searchTelemetry?: SearchTelemetryEvent[]
}

/**
 * Emit one telemetry event. Never throws; never blocks. Sinks:
 *  1. `window.__QT_OBS__.searchTelemetry` ring buffer (the runtime obs
 *     seam — inspectable in DevTools, scraped by e2e checks).
 *  2. A `quotech:search-telemetry` CustomEvent for host analytics.
 */
export function emitSearchTelemetry(event: SearchTelemetryEvent): void {
  if (typeof window === 'undefined') return
  const stamped: SearchTelemetryEvent = { ...event, ts: Date.now() }
  try {
    const w = window as unknown as { __QT_OBS__?: QtObsSlot }
    const obs = (w.__QT_OBS__ = w.__QT_OBS__ ?? {})
    const ring = (obs.searchTelemetry = obs.searchTelemetry ?? [])
    ring.push(stamped)
    if (ring.length > OBS_RING_MAX) ring.splice(0, ring.length - OBS_RING_MAX)
  } catch {
    /* observability must never break search */
  }
  try {
    window.dispatchEvent(
      new CustomEvent<SearchTelemetryEvent>(SEARCH_TELEMETRY_EVENT, {
        detail: stamped,
      }),
    )
  } catch {
    /* ditto */
  }
}

/**
 * f(surface) → a per-surface `query_resolved` emitter with built-in
 * dedupe, for the non-palette search layers (email inbox, kanban
 * filter box, jobs table). Dedupe is on (query, resultCount): polling
 * merges and re-renders that resolve the SAME query to the SAME count
 * emit once; a count change for the same query re-emits (new
 * information — e.g. late lane waves landing on the kanban board).
 * An empty/cleared query resets the dedupe and emits nothing.
 * Same never-throw guarantee as emitSearchTelemetry.
 */
export function createSearchQueryResolvedEmitter(
  surface: string,
): (input: { query: string; resultCount: number; rankParamSent?: boolean }) => void {
  let lastSignature: string | null = null
  return (input) => {
    try {
      const query = (input.query ?? '').trim()
      if (!query) {
        lastSignature = null
        return
      }
      const resultCount = Number.isFinite(input.resultCount)
        ? Math.max(0, Math.floor(input.resultCount))
        : 0
      const signature = `${query}\u0000${resultCount}`
      if (signature === lastSignature) return
      lastSignature = signature
      emitSearchTelemetry({
        kind: 'query_resolved',
        query,
        surface,
        resultCount,
        zeroResults: resultCount === 0,
        ...(input.rankParamSent !== undefined
          ? { rankParamSent: input.rankParamSent }
          : {}),
      })
    } catch {
      /* telemetry must never break the search UI path */
    }
  }
}
